import type { ReactNode, RefObject } from 'react';
import type { PublicRoomSnapshot } from '@sincronia/shared';
import { PlayFlash } from './PlayFlash.js';
import type { Flash } from '../../store/gameStore.js';
import { CardFace } from './PlayingCard.js';
import { GameTimer } from './Timer.js';
import { useT } from '../../i18n/index.js';
import { cn } from '../../lib/ui.js';

/**
 * Centro do tabuleiro: cronômetro, pilha e zona de soltura.
 * A zona de soltura é maior que a carta e pulsa quando um arrasto se aproxima.
 */
export function Board({
  snapshot,
  dropRef,
  overDrop,
  pending,
  flash,
}: {
  snapshot: PublicRoomSnapshot;
  dropRef: RefObject<HTMLDivElement | null>;
  overDrop: boolean;
  pending: boolean;
  flash: Flash | null;
}): ReactNode {
  const t = useT();
  const top = snapshot.topValue;

  return (
    <section className="relative flex min-h-0 flex-1 flex-col items-center justify-center gap-3 px-4">
      <GameTimer timer={snapshot.timer} paused={!snapshot.timer.running} />

      {/* A zona de soltura é dimensionada pela altura disponível: em janelas
          baixas ela encolhe em vez de empurrar a mão e os botões para fora. */}
      <div className="flex min-h-0 w-full flex-1 items-center justify-center">
        <div
          ref={dropRef}
          data-testid="drop-zone"
          data-over={overDrop ? 'true' : 'false'}
          aria-label={t('game.dropHere')}
          className={cn(
            'relative flex aspect-4/5 h-full max-h-[42vh] max-w-[70%] min-h-24 items-center justify-center',
            'rounded-[2rem] border-2 border-dashed transition-all duration-200',
            overDrop
              ? 'scale-105 border-pulse-400 bg-pulse-500/12 shadow-[0_0_60px_-12px_var(--color-pulse-500)]'
              : 'border-night-600 bg-night-900/40',
          )}
        >
          {overDrop ? (
            <span
              aria-hidden="true"
              className="animate-pulse-ring absolute inset-0 rounded-[2rem] border-2 border-pulse-400"
            />
          ) : null}

          {top === null ? (
            <p className="max-w-32 text-center text-sm text-night-400">{t('game.pileEmpty')}</p>
          ) : (
            <span
              data-testid="pile-top"
              data-value={top}
              className="flex h-full w-full items-center justify-center"
            >
              <CardFace
                key={`${snapshot.stateVersion}-${top}`}
                value={top}
                size="lg"
                flip
                elevated
                className="max-h-[78%] max-w-[78%]"
              />
            </span>
          )}

          {pending ? (
            <span className="absolute bottom-3 rounded-full bg-night-950/80 px-3 py-1 text-xs text-pulse-300">
              {t('game.waitingServer')}
            </span>
          ) : null}
        </div>
      </div>

      <p className="tabular text-xs text-night-400" data-testid="played-count">
        {t('game.played', { count: snapshot.playedCount })}
      </p>

      <PlayFlash flash={flash} players={snapshot.players} />
    </section>
  );
}
